import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Pause, PencilLine, Play, StopCircle } from "lucide-react";

import {
  activeSessionElapsedSeconds,
  formatDurationClock,
  formatDurationCompact,
  type TimeEntry,
} from "@zentrack/domain";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  PageHeader,
} from "@zentrack/ui";

import { useWebApp } from "../app/app-provider";
import { ClientEditorDialog } from "../components/client-editor-dialog";
import { EntryEditorDialog, type EntryEditorValues } from "../components/entry-editor-dialog";
import { ProjectEditorDialog } from "../components/project-editor-dialog";
import { StartTimerDialog } from "../components/start-timer-dialog";
import { projectLabel } from "../components/shared";
import { formatDateTime } from "../lib/date-helpers";

export function OverviewRoute() {
  const { api, perform, setErrorMessage, snapshot } = useWebApp();
  const [projectId, setProjectId] = useState("");
  const [startTimerDialogOpen, setStartTimerDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    if (!snapshot) {
      return;
    }

    if (snapshot.activeSession) {
      setProjectId(snapshot.activeSession.projectId);
      return;
    }

    if (!snapshot.projects.some((project) => project.id === projectId)) {
      setProjectId(snapshot.projects[0]?.id ?? "");
    }
  }, [projectId, snapshot]);

  useEffect(() => {
    if (!snapshot?.activeSession || snapshot.activeSession.pausedAt) {
      return;
    }

    const syncTick = () => setTick(Date.now());
    syncTick();
    const interval = window.setInterval(syncTick, 250);
    return () => window.clearInterval(interval);
  }, [snapshot?.activeSession?.pausedAt, snapshot?.activeSession?.startTime]);

  if (!snapshot) {
    return null;
  }

  const primaryClient = snapshot.clients[0] ?? null;
  const recentEntries = snapshot.timeEntries.slice(0, 5);
  const activeSession = snapshot.activeSession;
  const isPaused = Boolean(activeSession?.pausedAt);
  const timerProjectId = activeSession?.projectId ?? projectId;
  const timerProjectLabel = timerProjectId ? projectLabel(snapshot, timerProjectId) : null;
  const activeSoftwareRules = snapshot.projectSoftwareRules.filter(
    (rule) => rule.projectId === timerProjectId,
  );
  const hasSoftwareFilter = activeSoftwareRules.length > 0;
  const trackingWarning =
    hasSoftwareFilter && snapshot.trackingCapability.status !== "supported"
      ? snapshot.trackingCapability.message ??
        "Time tracking continues, but foreground-app filtering is unavailable in the browser."
      : null;
  const elapsedSeconds = activeSession
    ? activeSessionElapsedSeconds(
        activeSession.startTime,
        new Date(tick),
        activeSession.pausedDurationSeconds,
        activeSession.pausedAt,
      )
    : 0;
  const workspaceStats = [
    { label: "Clients", value: snapshot.clients.length },
    { label: "Projects", value: snapshot.projects.length },
    { label: "Saved sessions", value: snapshot.timeEntries.length },
  ];

  const handleStart = async (values: { note: string; projectId: string }) => {
    setErrorMessage(null);
    setTick(Date.now());
    await perform(() =>
      api.timer.start({
        note: values.note,
        projectId: values.projectId,
      }),
    );
    setProjectId(values.projectId);
    setStartTimerDialogOpen(false);
  };

  const handleStop = async () => {
    setErrorMessage(null);
    await perform(() => api.timer.stop());
  };

  const handlePauseToggle = async () => {
    if (!activeSession) {
      return;
    }

    setErrorMessage(null);
    setTick(Date.now());
    if (activeSession.pausedAt) {
      await perform(() => api.timer.resume());
      return;
    }

    await perform(() => api.timer.pause());
  };

  const handleEntrySave = async (values: EntryEditorValues) => {
    setErrorMessage(null);
    if (editingEntry) {
      await perform(() =>
        api.history.updateManualEntry({
          id: editingEntry.id,
          ...values,
        }),
      );
    } else {
      await perform(() => api.history.createManualEntry(values));
    }

    setEntryDialogOpen(false);
    setEditingEntry(null);
  };

  const handleClientCreate = async (name: string) => {
    setErrorMessage(null);
    await perform(() => api.clients.create({ name }));
    setClientDialogOpen(false);
  };

  const handleProjectCreate = async (
    name: string,
    softwareRules: typeof snapshot.projectSoftwareRules,
  ) => {
    if (!primaryClient) {
      return;
    }

    setErrorMessage(null);
    const project = await perform(() =>
      api.projects.create({
        clientId: primaryClient.id,
        name,
        softwareRules,
      }),
    );
    setProjectId(project.id);
    setProjectDialogOpen(false);
  };

  return (
    <div className="flex flex-col gap-5 pb-8">
      <PageHeader
        actions={
          activeSession ? (
            <div className="flex gap-2">
              <Button
                onClick={() => void handlePauseToggle()}
                type="button"
                variant={isPaused ? "default" : "outline"}
              >
                {isPaused ? <Play className="size-4" /> : <Pause className="size-4" />}
                {isPaused ? "Resume" : "Pause"}
              </Button>
              <Button onClick={() => void handleStop()} type="button" variant="outline">
                <StopCircle className="size-4" />
                Stop
              </Button>
            </div>
          ) : snapshot.projects.length > 0 ? (
            <Button onClick={() => setStartTimerDialogOpen(true)} type="button">
              <Play className="size-4" />
              Start timer
            </Button>
          ) : null
        }
        title="Overview"
      />

      <div className="flex flex-col gap-4">
        <Card className={activeSession ? "border-primary/25" : undefined}>
          <CardContent className="pb-5 pt-5">
            {snapshot.projects.length === 0 ? (
              <div className="flex flex-col gap-3">
                <p className="text-4xl font-semibold tracking-[-0.05em] tabular-nums text-muted-foreground">
                  00:00:00
                </p>
                <p className="text-sm text-muted-foreground">
                  Create a client and project to start tracking.
                </p>
                <Button
                  render={<Link to="/clients" />}
                  variant="outline"
                  className="w-fit"
                >
                  Go to clients
                </Button>
              </div>
            ) : activeSession ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col gap-1">
                    {timerProjectLabel ? (
                      <p className="text-xs text-muted-foreground">{timerProjectLabel}</p>
                    ) : null}
                    <p className="text-4xl font-semibold tracking-[-0.05em] tabular-nums text-foreground">
                      {formatDurationClock(elapsedSeconds)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isPaused ? "Paused since" : "Started"} {formatDateTime(activeSession.startTime)}
                    </p>
                  </div>
                  <Badge tone={isPaused ? "neutral" : "accent"} className="mt-1">
                    {isPaused ? "Paused" : "Running"}
                  </Badge>
                </div>

                {(activeSession.note || hasSoftwareFilter) && (
                  <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-border/60 pt-3 text-sm">
                    {activeSession.note ? (
                      <div className="min-w-0">
                        <span className="text-muted-foreground">Note: </span>
                        <span className="text-foreground">{activeSession.note}</span>
                      </div>
                    ) : null}
                    {hasSoftwareFilter ? (
                      <div className="min-w-0">
                        <span className="text-muted-foreground">Apps: </span>
                        <span className="truncate text-foreground">
                          {activeSoftwareRules.map((rule) => rule.displayName).join(", ")}
                        </span>
                      </div>
                    ) : null}
                  </div>
                )}

                {trackingWarning ? <Banner message={trackingWarning} tone="warning" /> : null}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col gap-1">
                    {snapshot.projects.length > 0 && projectId ? (
                      <p className="text-xs text-muted-foreground">
                        {projectLabel(snapshot, projectId)}
                      </p>
                    ) : null}
                    <p className="text-4xl font-semibold tracking-[-0.05em] tabular-nums text-muted-foreground">
                      00:00:00
                    </p>
                  </div>
                  <Badge tone="neutral" className="mt-1">
                    Idle
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between pb-0">
            <p className="text-sm font-medium text-foreground">Workspace</p>
            <div className="flex gap-1.5">
              <Button render={<Link to="/clients" />} size="sm" variant="ghost">
                Manage
              </Button>
              <Button onClick={() => setClientDialogOpen(true)} size="sm" type="button" variant="ghost">
                Add client
              </Button>
              <Button
                disabled={!primaryClient}
                onClick={() => setProjectDialogOpen(true)}
                size="sm"
                type="button"
                variant="ghost"
              >
                Add project
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-3 divide-x divide-border/60">
              {workspaceStats.map((item) => (
                <div className="flex flex-col gap-0.5 px-4 first:pl-0 last:pr-0" key={item.label}>
                  <p className="text-[11px] text-muted-foreground">{item.label}</p>
                  <p className="text-xl font-semibold tracking-[-0.03em] text-foreground">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between border-b border-border/60 pb-3">
          <p className="text-sm font-medium text-foreground">Recent sessions</p>
          <Button render={<Link to="/history" />} size="sm" variant="ghost">
            View all
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {recentEntries.length === 0 ? (
            <EmptyState
              className="mt-4"
              description="Start the timer to record your first session."
              icon={Play}
              title="No finished sessions yet"
            />
          ) : (
            <div className="divide-y divide-border/50">
              {recentEntries.map((entry) => (
                <div className="flex items-center justify-between gap-4 py-3" key={entry.id}>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">
                        {projectLabel(snapshot, entry.projectId)}
                      </p>
                      {entry.countedDurationSeconds <
                      Math.round(
                        (new Date(entry.endTime).getTime() -
                          new Date(entry.startTime).getTime()) /
                          1000,
                      ) ? (
                        <Badge tone="warning">Filtered</Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {entry.note ? entry.note + " · " : ""}
                      {formatDateTime(entry.startTime)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <p className="text-sm font-medium tabular-nums text-foreground">
                      {formatDurationCompact(entry.countedDurationSeconds)}
                    </p>
                    <Button
                      onClick={() => {
                        setEditingEntry(entry);
                        setEntryDialogOpen(true);
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <PencilLine className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <StartTimerDialog
        initialProjectId={projectId || snapshot.projects[0]?.id}
        onOpenChange={setStartTimerDialogOpen}
        onStart={handleStart}
        open={startTimerDialogOpen}
        projectLabel={(value) => projectLabel(snapshot, value)}
        projectSoftwareRules={snapshot.projectSoftwareRules}
        projects={snapshot.projects}
        trackingCapability={snapshot.trackingCapability}
      />

      <ClientEditorDialog
        onOpenChange={setClientDialogOpen}
        onSave={handleClientCreate}
        open={clientDialogOpen}
        title="New client"
      />

      {primaryClient ? (
        <ProjectEditorDialog
          clientName={primaryClient.name}
          onOpenChange={setProjectDialogOpen}
          onSave={handleProjectCreate}
          open={projectDialogOpen}
          title="New project"
          trackingCapability={snapshot.trackingCapability}
        />
      ) : null}

      <EntryEditorDialog
        defaultProjectId={projectId || snapshot.projects[0]?.id}
        entry={editingEntry}
        onOpenChange={(open) => {
          setEntryDialogOpen(open);
          if (!open) {
            setEditingEntry(null);
          }
        }}
        onSave={handleEntrySave}
        open={entryDialogOpen}
        projectLabel={(value) => projectLabel(snapshot, value)}
        projects={snapshot.projects}
      />
    </div>
  );
}
